import { Baseline, BaselineLite, BaselineValue, BaselineValueLite, DataPack, DataPackSet, EntityModificationTrace, FormulaEngineInstance, FreeDataValue, VisionStep, WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { clone, cloneDeep } from 'lodash-es';
import { WizConstants } from '../../model/constants';
import { COMPUTE_MODE, DATA_KIND, EM_ACTION, FIELD_KIND } from '../../model/enums';
import { AkmFDVCell, IBaseline, IBaselineValue, SisterResult, WidgetLineUpdatePack } from '../../model/interfaces';
import { Model } from '../../model/model';
import { ScenarioVO } from '../../vo/scenarii/scenario.vo';
import { VisionVO } from '../../vo/vision/vision.vo';
import { DataLoadObj } from '../../vo/workingdata/dataload-obj';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { FreedataUpdatePack } from '../../vo/workingdata/update-pack';

import { WidgetCell, WidgetLine } from '../../vo/workingdata/widget-line.obj';
import { FormulaCoreEngine } from '../computer/formula.core.engine';
import { BaselineEngine } from '../workingdata/baseline.engine';
import { DataLooper, LoopResult, LoopVO } from './data.looper';

/**
 * Moteur d'édition des données pour le moteur
 */
export class DataEngine {

  /* Le modèle pour l'accés aux autres engines */
  protected _model: Model;
  constructor(pmodel: Model) {
    this._model = pmodel;
  }

  public get model(): Model {
    return this._model as Model;
  }

  
  /* ##############################################################################
        détection d'un changement de locale pour recalcul ciblé
        ou mieux, alimentation des valeurs localisées
        L'écouteur est posé lors de la création du FEI
  ############################################################################### */

  /**
   * changement de locale: recalcul éventuel des mesures du fei.translatableModeMatrix
   * @param e: wizEvent 
   */
  public localizeMe(e: any): void {
    // à overrider
  }

  /* ##############################################################################
          gestion crossVision : les FEIs en dépendances
  ############################################################################### */


  /** la map des crossVision et leur statut */
  public crossVisions: Map<string, string> = new Map();

  /**
   * pour socket.message, inutile de checker si pas de crossVision
   */
  public hasCrossed(): boolean {
    return this.crossVisions?.size > 0;
  }

  /** 
   * map des baselines liés sur d'autres visions : vision | level | scenario | key (baseline)  = bl 
  */
  protected _crossedLinesMap: Map<string, Map<number, Map<string, Map<string, Baseline>>>> = new Map();


  /**
   * accesseur sur une baseline liée (crossVision)
   * @param vision 
   * @param level 
   * @param scen 
   * @param key 
   * @returns 
   */
  public getCrossedLine(vision: string, level: number, scen: string, key: string): Baseline {
    return this._crossedLinesMap.get(vision)?.get(level)?.get(scen)?.get(key) || null;
  }

  /**
   * suppression une baseline liée (crossVision)
   * @param vision 
   * @param level 
   * @param scen 
   * @param key 
   * @returns 
   */
  public deleteCrossedLine(vision: string, level: number, scen: string, key: string): void {
    this._crossedLinesMap.get(vision)?.get(level)?.get(scen)?.delete(key) || null;
  }

  /**
   * ajout / modif d'une bl liée
   * @param bl 
   */
  public addOrUpdateCrossedLine(bl: Baseline): void {
    let vision: string = bl.v;
    let level: number = bl.level;
    let scen: string = bl.scen;
    if (!this._crossedLinesMap.has(vision)) {
      this._crossedLinesMap.set(vision, new Map());
    }
    if (!this._crossedLinesMap.get(vision).has(level)) {
      this._crossedLinesMap.get(vision).set(level, new Map());
    }
    if (!this._crossedLinesMap.get(vision).get(level).has(scen)) {
      this._crossedLinesMap.get(vision).get(level).set(scen, new Map());
    }
    this._crossedLinesMap.get(vision).get(level).get(scen).set(bl.key, bl);
  }



  /**
   * alimentation de la map de lignes crossed (par widgetDataService)
   * @param vision 
   * @param level 
   * @param scen 
   * @param key 
   * @returns 
   */
  public feedCrossedLines(dlo: DataLoadObj): void {
    let vision: string = dlo.visionStep.visionCode;
    let level: number = dlo.visionStep.level;
    let scen: string = dlo.scenario;
    let bls: { [key: string]: Baseline } = dlo.localBaselines;
    
    if (!this._crossedLinesMap.has(vision)) {
      this._crossedLinesMap.set(vision, new Map());
    }
    if (!this._crossedLinesMap.get(vision).has(level)) {
      this._crossedLinesMap.get(vision).set(level, new Map());
    }
    if (!this._crossedLinesMap.get(vision).get(level).has(scen)) {
      this._crossedLinesMap.get(vision).get(level).set(scen, new Map());
    }
    let targetMap: Map<string, Baseline> = this._crossedLinesMap.get(vision).get(level).get(scen);

    Object.values(bls).forEach(bl => {
      targetMap.set(bl.key, bl);
    });

    let crossKey: string = [vision, level].join(WizConstants.CONTEXT_SEPARATOR);
    this.crossVisions.set(crossKey, DataLoadObj.COMPLETE);
    // console.log('feedCrossedLines');
  }


  /* ##############################################################################
          gestion des FEIs
  ############################################################################### */

  /** 
   * map des FEIs clé : Mode(compute/local) | level | scenario  = FEI 
   * en cas de fake, le scenario est SCENAR#FAKE
  */
  private _feiMap: Map<string, Map<number, Map<string, FeiVO>>> = new Map();

  /**
   * accesseur sur la map des FEIs
   * clé : level | scenario
   */
  public get feiMap(): Map<string, Map<number, Map<string, FeiVO>>> {
    return this._feiMap;
  }

  /**
   * retourne la map de FEIs à plat pour traitements en masse
   * Uniquement les FEIs en mode LOCAL
   * @returns EngineFeiVO[]
   */
  public flatFeiCollection(): FeiVO[] {
    let result: FeiVO[] = [];
    [...this._feiMap?.get(COMPUTE_MODE.LOCAL)?.values()].forEach(sub => {
      [...sub.values()].forEach(fei => {
        result.push(fei);
      })
    })
    return result;
  }

  /**
   * accesseur sur le fei 
   * @param mode compute
   * @param level cible
   * @param scen scenario cible
   * @param fake calcul volatile (forms)
   * @returns le fei
   */
  public getFei(mode: string, level: number, scen: string,): FeiVO {
    const scenKey: string = scen;
    return this._feiMap.get(mode)?.get(level)?.get(scenKey);
  }

  /**
   * alimentation de la map de FEI
   * clé : level | scenario  = FEI 
   * en cas de fake, le scenario est SCENAR#FAKE
   * @param fei
   */
  public feedFei(fei: FeiVO): void {
    const scenKey: string = fei.scenario;
    if (!this._feiMap.has(fei.computeMode)) {
      this._feiMap.set(fei.computeMode, new Map());
    }
    if (!this._feiMap.get(fei.computeMode).has(fei.visionStepLevel)) {
      this._feiMap.get(fei.computeMode).set(fei.visionStepLevel, new Map());
    }
    this._feiMap.get(fei.computeMode).get(fei.visionStepLevel).set(scenKey, fei);
  }

  /**
   * suppression des FEI chargés
   */
  public clearFei(): void {
    this._feiMap = new Map();
  }

  /* ##############################################################################
          utilitaires
  ############################################################################### */

  /**
   * détermine si la donnée  est numérique, pour sorts
   * @param dataKind
   * @returns true selon le datakind
   */
  public datakindIsNumeric(datakind: string): boolean {
    let isNumeric: boolean = false;
    if (!!datakind) {
      switch (datakind) {
        case DATA_KIND.INT:
        case DATA_KIND.NUMBER:
        case DATA_KIND.DATE:
        case DATA_KIND.TIME:
        case DATA_KIND.BOOLEAN:
          isNumeric = true;
          break;
      }
    }
    return isNumeric;
  }

  /* ##############################################################################
          Création des Feis et calcul des dépendances
  ############################################################################### */

  /**
   * Création d'un nouvel FEI
   */
  protected newFeiVO(source: FormulaEngineInstance, v: VisionVO): FeiVO {
    return new FeiVO(source, this.model, v, source.visionStep, source.scenario);
  }

  /**
  * création d'un nouveau FEI sur alimentation de la DS d'un widget
  * Va déclencher les contrôles de dépendances et les demandes de chargement
  * @param v 
  * @param vs 
  * @param scenario
  * @param configuration: WidgetConfigurationObj (pour override dans common)
  * @returns le feiVO
  */
  public generateFei(v: VisionVO, vs: VisionStep, scenario: string, configuration: WidgetConfigurationObj): FeiVO {
    const proto: FormulaEngineInstance = new FormulaEngineInstance();
    proto.vision = v.source;
    if (!vs) {
      console.log('no vs', this);
    }
    proto.visionStep = vs;
    proto.scenario = scenario;
    let fei: FeiVO = this.newFeiVO(proto, v);
    
    // mémorisation du mode compute. Ici on est dans COMPUTE. C'est donc toujours "LOCAL" (oui pour COMPUTE, "LOCAL", c'est COMPUTE)
    fei.computeMode = COMPUTE_MODE.LOCAL;
    
    // construction de la map de clés du FEI
    BaselineEngine.buildKeys(fei);

    this.model.dataEngine.feedFei(fei); // enregistrement dans la map de FEI
    this.feedDependencies(fei, configuration); // alimentation de la matrice de dépendances du fei

    return fei;
  }

  /**
  * alimente les dépendances sur les autres Fei pour ce fei
  * va déclencher les appels au WidgetDataservice
  * @param fei
  * @param configuration: WidgetConfigurationObj
  */
  protected feedDependencies(fei: FeiVO, configuration: WidgetConfigurationObj): void {


    // construction des dépendances de scenarii
    this.feedScenarioDependencies(fei);

    // dépendances de niveaux
    fei.model.measureEngine.buildFeiMatrix(fei); // construction de la matrice de calcul et collecte des dépendances de niveau

    // on va charger MAIN pour mon niveau, toujours.
    this.addOneDependency(fei, fei.visionStepLevel, WizConstants.BASE_SCENARIO, configuration);

    // on ajoute les dépendances même niveau sur les scenar
    fei.requiredScenario.forEach((scen: string) => {
      this.addOneDependency(fei, fei.visionStepLevel, scen, configuration);
    });

    // on ajoute les dépendances de step sur le même scenar (sauf si je suis multiScenar, sera forcément géré en masse)
    if (fei.scenario !== WizConstants.SCEN_KEY_CODE) {
      fei.requiredStep.forEach((level: number) => {
        this.addOneDependency(fei, level, fei.scenario, configuration);
      });
    }

    // collecte des crossVision
    this.feedCrossVision(fei);


    // on ajoute tout le reste (les scenar pour les autres step)
    fei.requiredStep.forEach((level: number) => {
      fei.requiredScenario.forEach((scen: string) => {
        // si pas déjà ajoutée
        let exist: FeiVO = fei.requiredFei.get(level)?.get(scen);
        if (!exist) {
          this.addOneDependency(fei, level, scen, configuration);
        }
      });
    });
  }

  /**
   * ajout d'une dépendance de fei sur le fei en cours
   * et génération de la dépendance si nécessaire
   * @param fei 
   * @param level de la dépendance
   * @param scen de la dépendance
   * @param configuration: WidgetConfigurationObj pour le mode de calcul 
   */
  private addOneDependency(fei: FeiVO, level: number, scen: string, configuration: WidgetConfigurationObj): void {
    let dependency: FeiVO = this.getFei(COMPUTE_MODE.LOCAL, level, scen);
    if (!dependency) { // la dépendance n'existe pas, on va la construire et demander son chargement
      dependency = this.generateFei(fei.vision,
        BaselineEngine.getVisionStepByLevel(fei.vision, level), scen, configuration);
      dependency.computeMode = fei.computeMode; // le mode de calcul est contagieux
    }
    if (!fei.requiredFei.has(level)) {
      fei.requiredFei.set(level, new Map());
    }
    fei.requiredFei.get(level).set(scen, dependency); // ajout aux dépendances du FEi

  }

  /**
   * va alimenter requiredScenario et parentScenariiPile, la pile de scenarii parents sur le FEI
   * @param fei 
   */
  private feedScenarioDependencies(fei: FeiVO): void {

    let flatScens: string[] = []; // les scenarii nécessaires
    let parentPile: string[] = null; // la pile de parent (sauf pour les multiscen)

    if (fei.scenario === WizConstants.SCEN_KEY_CODE) { // mode multiscenar, on va ajouter tous les fei
      flatScens = this.model.scenarioEngine.getAllScens(fei.visionCode);
    } else { // sinon, juste la pile de dépendances
      flatScens = this.model.scenarioEngine.getScenParents(fei.visionCode, fei.scenario);
      flatScens.push(fei.scenario);

      // construction de la pile de parents
      parentPile = [];
      let scen: ScenarioVO = this.model.scenarioEngine.getScenario(fei.visionCode, fei.scenario); // moi
      while (!!scen && scen.parentCode) {
        parentPile.push(scen.parentCode);
        scen = this.model.scenarioEngine.getScenario(fei.visionCode, scen.parentCode);
      }
      fei.parentScenariiPile = parentPile;

    }
    fei.requiredScenario = new Set(flatScens); // alimentation des dépendances de scenarii sur le fei
    fei.requiredScenario.delete(fei.scenario); // pas de dépendance self
  }

  /**
 * ##### étape 1
 * appelé par le widgetDataService sur fin de chargement d'un dataload
 * contrôle que toutes les dépendances sont disponibles
 * @param dlo l'objet de chargement des données
 */
  public checkAllDependenciesReady(dlo: DataLoadObj): void {
    
    if(!dlo.crossed) { // pas pour les crossed, ce sont juste des sides
      let loadedFei: FeiVO = this.getFei(COMPUTE_MODE.LOCAL, dlo.visionStep.level, dlo.scenario);
      loadedFei.linesLoaded = true;

      // contrôle unitaire
      this.checkDependencies(loadedFei);
    }


    // le tour de boucle a déclenché une modification
    let hasUpdate: boolean = false;

    // sur fin de chargement de données, contrôle des dépendances pour un fei
    do { // on boucle en cas de modif... Si un enfant et OK, je le suis peut-être aussi...
      hasUpdate = false;
      this.flatFeiCollection().forEach((fei: FeiVO) => {
        // il manque encore les données sources pour des dépendances ET les lignes sont chargés
        if (!!fei.linesLoaded && !fei.allDependenciesReady) {
          // console.log('checkAllDependenciesReady loop', fei);
          if (this.checkDependencies(fei)) {
            hasUpdate = true;
          }
         }
      });
    } while (hasUpdate);

  }

  /**
   * contrôle de l'état des dépendances pour un FEI
   * Va alimenter scenariiDependenciesLoaded et stepDependenciesLoaded
   * @param fei 
   * @return true si changement d'état sur le fei
   */
  private checkDependencies(fei: FeiVO): boolean {

    let hasChange: boolean = false; // un changement dans l'état

    let allscenOK: boolean = true;
    let allstepOK: boolean = true;
    let allcrossOK: boolean = true;

    [...fei.requiredFei.keys()].forEach((level: number) => {
      [...fei.requiredFei.get(level).keys()].forEach((scen: string) => {
        let deps: FeiVO = fei.requiredFei.get(level).get(scen);
        // console.log('Me: ', fei.visionStepLevel, fei.scenario, ' ###### deps: ', deps.visionStepLevel, deps.scenario, deps.linesLoaded);

        if (level === fei.visionStepLevel && !deps.linesLoaded) { // même niveau
          allscenOK = false;
          // console.log('missing scen',fei.uniqueName, deps.visionStepLevel, deps.scenario, deps.linesLoaded);
        }

        // autres dépendances (multiscen et héritages)
        if (scen === fei.scenario && !deps.linesLoaded) { // même niveau
          allstepOK = false;
          // console.log('missing step',fei.uniqueName, deps.visionStepLevel, deps.scenario, deps.linesLoaded);
        }

      });
    });

    [...fei.requiredCrossed.keys()].forEach(crossedKey => {
      let state: string = fei.model.dataEngine.crossVisions.get(crossedKey);
      if(state !== DataLoadObj.COMPLETE) {
        allcrossOK = false;
      }
    });



    if (!fei.scenariiDependenciesLoaded && allscenOK) { // tous les scenarii sont maintenant chargés
      fei.scenariiDependenciesLoaded = true;
      hasChange = true;
    }
    if (!fei.stepDependenciesLoaded && allstepOK) { // tous les scenarii sont maintenant chargés
      fei.stepDependenciesLoaded = true;
      hasChange = true;
    }

    if (!fei.crossDependenciesLoaded && allcrossOK) { // tous les scenarii sont maintenant chargés
      fei.crossDependenciesLoaded = true;
      hasChange = true;
    }

    if (fei.scenariiDependenciesLoaded && fei.stepDependenciesLoaded && fei.crossDependenciesLoaded) { // on marque le FEI
      fei.allDependenciesReady = true;

    }
    return hasChange; // un changement détecté ? si oui, nouveau tour de boucle
  }

  /* ##############################################################################
          Calcul du pool de données
  ############################################################################### */

  /**
  * construction du pool de lignes (widgetLine) pour ce FEI, avec prise en compte des héritages de scenarii.
  * Ici, on ne gére pas les fei multiscens. On gére uniquement les scenarii non multiples
  * @param fei 
  * @param doCompute ? lancer le calcul si true. False depuis computeEngine si les données sont en cache
  * @param silent ? mode silencieux pour le calul (si calcul)
  * @returns true si le pool de WidgetLines a été construit
  */
  public feedWorkingPool(fei: FeiVO, doCompute: boolean = true, silent: boolean = false): boolean {

    let hasUpdate: boolean = false; // flag de détection de mise à jour, pour déclencher un nouveau tour de boucle
    const lines: Map<string, Map<string, WidgetLine>> = new Map(); // les widgetLines à construire scen = key = WL

    // mécanisme différent pour les multiScens
    if (fei.scenario === WizConstants.SCEN_KEY_CODE) {

      let allAndMain: Set<string> = new Set([...fei.requiredScenario]);
      allAndMain.add(WizConstants.BASE_SCENARIO); // on ajoute MAIN, on ne le trace pas dans les piles de dépendances


      // if faut que tout le monde soit la (toutes les lignes pour tous les scenarii)
      // et on ne prend que les lignes QUI EXISTENT vraiment au niveau du scenario (rien si aucun héritage)
      let allIn: boolean = true;
      let requiredFei: FeiVO;
      // console.log('feedWorkingPool multi', fei.uniqueName, fei);
      allAndMain.forEach((scenkey: string) => {
        requiredFei = this.getFei(fei.computeMode, fei.visionStepLevel, scenkey);
        if (!requiredFei?.workingPoolReady) { // mon héritage n'est pas prêt, j'attends
          allIn = false;
          return; // on sort de la boucle
        }
      });

      if (allIn) { // tout le monde est dispo 
        // on merge en gardant le lien vers le scenario source (pas d'écrasement des lines)       
        allAndMain.forEach((scenkey: string) => {
          requiredFei = this.getFei(fei.computeMode, fei.visionStepLevel, scenkey);
          requiredFei.lines.get(requiredFei.scenario).forEach((wl: WidgetLine) => {
            // if(wl.scen === wl.bl?.scen) { // la ligne n'est pas un pur override
            if (!lines.has(wl.scen)) {
              lines.set(wl.scen, new Map());
            }
            lines.get(wl.scen).set(wl.key, wl);
            // }
          });
        });
        // console.log('feedWorkingPool ok', fei.uniqueName, lines);
        fei.lines = lines; // alimentation des lignes
        fei.workingPoolReady = true; // flag, il est complet
        hasUpdate = true;
      }

    } else { // construction des lignes pour les scenarii non multiples

      // construction des lignes pour les scenarii non multiples
      // mon scenario
      const myScenario: string = fei.scenario;
      // papa, s'il existe
      let previousScenario: string = null;
      if (fei.parentScenariiPile?.length > 0) { // j'ai bien un parent (sinon, je suis MAIN)
        previousScenario = fei.parentScenariiPile[0];
      }

      if (!!previousScenario) { // j'ai un héritage
        let previousFEI: FeiVO = this.getFei(fei.computeMode, fei.visionStepLevel, previousScenario);
        if (!previousFEI.workingPoolReady) { // mon héritage n'est pas prêt, j'attends
          return hasUpdate; // retour de méthode, pas prêt
        }
        // alimentation des lignes par reprise brute de l'existant sur le scenar précédent
        this.clonePreviousScenarioLine(lines, previousFEI, fei);
      }

      // collecte de mon dataload pour prise de MES Baselines
      const dlo: DataLoadObj = this.model.loadDataService.getDataloadByKey(fei.visionCode, fei.visionStepLevel, myScenario);

      let wl: WidgetLine; // la widgetLine à construire
      if (!lines.has(myScenario)) { // si aucune reprise, donc au moins toujours sur MAIN
        lines.set(myScenario, new Map());
      }

      // prise des BLs persitées
      const linesFromBDD: Set<string> = FormulaCoreEngine.getPersitedMeasures(fei);

      // alimentation de MES Bls et merge éventuel
      Object.keys(dlo.localBaselines).forEach((blKey: string) => {

        // création de ma widgetLine par prise des valeurs de la baseline
        let bl: Baseline = dlo.localBaselines[blKey];
        if(!!bl) {
          wl = this.baselineToWidgetLine(fei, myScenario, bl);

          // et merge pour alimenter les valeurs héritées
          if (!!previousScenario) { // inutile si pas d'héritage
            this.mergeWidgetLine(fei, lines.get(myScenario).get(wl.key), wl, linesFromBDD);
          }
  
          lines.get(myScenario).set(wl.key, wl); // Ajout au pool
        } else {
          console.log('missing bl ?',dlo.uniqueKey, blKey);
        }

      });

      fei.lines = lines;
      fei.workingPoolReady = true; // passage à l'état suivant
      // Ordre de calcul
      if(doCompute) {
        this.computeFei(fei, silent);
        hasUpdate = true;
      }

    }

    return hasUpdate; // retour de méthode
  }

  /* ##############################################################################
          Moteur de calcul
  ############################################################################### */

  /**
  * Déclenchement d'un calcul
  * On ne tient pas compte ici des multi-scen. 
  * @param fei 
  * @param silent mode silencieux pour le calcul
  */
  protected computeFei(fei: FeiVO, silent: boolean = false): void {
    if (!fei.computed && fei.scenario !== WizConstants.SCEN_KEY_CODE) { // pas encore calculé
      let dlo: DataLoadObj = this.model.loadDataService.getDataloadByKey(fei.visionCode, fei.visionStepLevel, fei.scenario);

      dlo.startTime = new Date().getTime(); // début mesure
      let lines: WidgetLine[] = [...fei.lines.get(fei.scenario).values()];

      FormulaCoreEngine.computeLines(fei, lines, silent); // calcul en mode non-silencieux      

      dlo.endTime = new Date().getTime(); // fin mesure
      dlo.totalComputeTime = (dlo.endTime - dlo.startTime) / 1000;
      dlo.state = DataLoadObj.COMPLETE; // on a calculé au moins une fois
      fei.computed = true;
    }

  }

  /**
   * Vérification et mise à jour d'un fei multiscen
   * Passage éventuel à computed sur les multiScen qui disposent de tous leurs FEI chargés
   * @param fei le fei multiscen
   * @returns un booléen indiquant si le fei multiscen a été mis à jour à computed
   */
  public checkMultiscen(fei: FeiVO): boolean {
    if (fei.scenario === WizConstants.SCEN_KEY_CODE) {

      // Si le pool de travail n'est pas prêt, alors on tente de le construire
      if (!fei.workingPoolReady) {
        this.feedWorkingPool(fei);
      }

      // Si le FEI n'est pas flaggué à computed, alors on lance la vérification
      // Si tous les enfants du FEI multiscen sont computed, alors notre multiscen peut passer à computed
      if (!fei.computed) {
        // je contrôle que toutes ses dépendances sont computed
        let allAndMain: Set<string> = new Set([...fei.requiredScenario]);
        allAndMain.add(WizConstants.BASE_SCENARIO); // on ajoute MAIN, on ne le trace pas dans les piles de dépendances

        let allIn: boolean = true;
        let requiredFei: FeiVO;

        // on merge en gardant le lien vers le scenario source (pas d'écrasement des lines)       
        allAndMain.forEach((scenkey: string) => {
          requiredFei = this.model.dataEngine.getFei(fei.computeMode, fei.visionStepLevel, scenkey);
          if (!requiredFei.computed) {
            // console.log('checkMultiscen requiredFei missing', fei.uniqueName, requiredFei.uniqueName);
            allIn = false;
          }
        });

        // console.log('checkMultiscenComputed', fei.uniqueName, allIn);

        if (allIn) {
          fei.computed = true;
          return true;
          // console.log('checkMultiscen ok', fei.uniqueName);
        }
      }
    }

    return false;
  }

  /**
   * Vérification et mise à jour des feis multiscen d'une liste de feis
   * Passage éventuel à computed sur les multiScens qui disposent de tous leurs FEI chargés
   * @param feis les feis à vérifier
   */
  public checkMultiscens(feis: FeiVO[]): void {
    feis.forEach((fei: FeiVO) => {
      this.checkMultiscen(fei);
    });
  }

  /* ##############################################################################
          Manipulation des widget lines & base lines
  ############################################################################### */

  /**
   * va alimenter la ligne target avec les cellules de la ligne source 
   * QU'ELLE NE POSSEDE PAS ! ou DONT LE SCENARIO EST INFERIEUR
   * @param fei
   * @param sourceLine 
   * @param targetLine 
   * @param targetMeasures les mesures en BDD uniquement, pas les calculs
   * @param checkScen, utilisé sur retour de commit. Pas d'intérêt lors de la construction initiale
   * @param deadLineScenario le scenario en cours de suppression. Si je suis lui, je prend l'antécédent
   */
  public mergeWidgetLine(fei: FeiVO, sourceLine: WidgetLine, targetLine: WidgetLine, targetMeasures: Set<string>,
    checkScen: boolean = false,
    deadLineScenario: string = null
  ): void {
    if (!sourceLine) {
      return;
    }
    // on merge
    // TODO TODO en cas d'écrasement de la valeur de la source, reprendre ses .ch ?
    [...sourceLine.cells.values()].forEach((sourceCell: WidgetCell) => {
      if (targetMeasures.has(sourceCell.m)) {
        if (!targetLine.cells.has(sourceCell.m)) { // existe pas
          targetLine.cells.set(sourceCell.m, sourceCell);
        } else if (checkScen && deadLineScenario && targetLine.cells.get(sourceCell.m).sc === deadLineScenario) {
          // sur suppression, j'utilise le scenario du mort...
          targetLine.cells.set(sourceCell.m, sourceCell);
        } else if (checkScen && this.isBetterScen(fei, sourceCell, targetLine.cells.get(sourceCell.m))) { // meilleur scenar
          targetLine.cells.set(sourceCell.m, sourceCell);
        }
      }
    });
  }

  /**
   * alimentation des lignes avec reprise brute des lignes du fei précédent
   * on va cloner la widgetLine, mais garder le lien vers les instances de BLs et BLVs
   * @param lines les lignes (WidgetLine) à alimenter
   * @param sourceFei le FEI du scenario précédent
   * @param targetFei ma cible, pour prise du code SCEN à alimenter sur les lines
   */
  private clonePreviousScenarioLine(lines: Map<string, Map<string, WidgetLine>>, sourceFei: FeiVO, targetFei: FeiVO): void {

    let wl: WidgetLine;
    lines.set(targetFei.scenario, new Map());
    if (!sourceFei.lines?.has(sourceFei.scenario)) {

    }

    // construction de mes lignes sur la base du previous
    [...sourceFei.lines.get(sourceFei.scenario)?.values()].forEach((src: WidgetLine) => {
      wl = {
        key: src.key,
        binKey: src.binKey,
        scen: targetFei.scenario,
        level: targetFei.visionStepLevel,
        uk: [targetFei.scenario, src.key].join(WizConstants.KEY_SEPARATOR),
        bl: src.bl,
        cells: new Map(),
      };

      // on ne prend que les cellules éditables
      [...src.cells.keys()].forEach((fkdKey: string) => {
        if (sourceFei.editableKeys.has(fkdKey)) {
          wl.cells.set(fkdKey, src.cells.get(fkdKey));
        }
      });

      //  wl = clone(src); 
      // wl.scen = targetFei.scenario; // on force le scenario, c'est toujours celui du FEI
      lines.get(targetFei.scenario).set(wl.key, wl);
    });
  }

  /**
   * comparer les scenar de 2 cellules pour éventuel remplacement
   * @param source l'origine (la cellule de la ligne previous)
   * @param target la WidgetCell à éventuellement modifier
   */
  private isBetterScen(fei: FeiVO, source: WidgetCell, target: WidgetCell): boolean {
    let result: boolean = false;
    let sourceIdx: number = fei.parentScenariiPile.indexOf(source.sc);
    let targetIdx: number = fei.parentScenariiPile.indexOf(target.sc);
    if (sourceIdx < targetIdx) { // plus haut dans la pile, plus proche de moi
      result = true;
    }
    return result;
  }


  /**
  * encapsulation d'une Baseline en widgetLine
  * @param scenario 
  * @param bl 
  * @returns la WidgetLine
  */
  public baselineToWidgetLine(fei: FeiVO, scenario: string, bl: Baseline): WidgetLine {
    let cells: Map<string, WidgetCell> = new Map();

    // construction des cellules
    let wc: WidgetCell;

    if (!!bl.values) {
      Object.values(bl.values).forEach((src: BaselineValue) => {

        // inscription de la baseline dans la matrice, elle porte la valeur
        this.model.cellMatrixEngine.addCell(src.level, src.key, src.scen, src.mkey, src.valor)

        // wc = { mkey: src.mkey, scen: scenario, level: bl.level, src};
        wc = { k: src.key, m: src.mkey, sc: scenario, l: bl.level };

        cells.set(wc.m, wc);
      });
    }

    // construction de la ligne
    let wl: WidgetLine = {
      key: bl.key,
      uk: [scenario, bl.key].join(WizConstants.KEY_SEPARATOR),
      binKey: this.generateBinaryKey(bl.id),
      level: bl.level,
      scen: scenario,
      bl,
      cells
    }
    return wl;
  }

  /**
     * construction de la clé d'unicité binaire d'une bl
     * @param bl cible
     * @returns sa clé
     */
  private generateBinaryKey(id: number): string {
    const hundredPart: number = Math.floor(id / 50);
    const decimalPart: number = id % 50;
    const binRankPart = Math.pow(2, decimalPart);
    return hundredPart + WizConstants.CONTEXT_SEPARATOR + binRankPart;
  }

  /**
     * Collecte sous la forme d'une map des widgetlines pour un level et un scenario donné.
     * @param stepLevel le level du step (non obligatoire)
     * @param scenario le scénario (non obligatoire)
     * @returns la map des widgetlines
     */
  public getAllLines(stepLevel?: number, scenario?: string): Map<number, Map<string, Map<string, WidgetLine>>> {
    const resultMap: Map<number, Map<string, Map<string, WidgetLine>>> = new Map();

    this.flatFeiCollection().filter((fei: FeiVO) => {
      return (!stepLevel || fei.visionStepLevel === stepLevel) && (!scenario || fei.scenario === scenario);
    }).forEach((fei: FeiVO) => {

      if (!resultMap.has(fei.visionStepLevel)) resultMap.set(fei.visionStepLevel, new Map());
      const resultStepLevelMap: Map<string, Map<string, WidgetLine>> = resultMap.get(fei.visionStepLevel);

      fei.lines?.forEach((scenMap, scenario) => {
        resultStepLevelMap.set(scenario, scenMap);
      });

    });

    return resultMap;
  }

  /**
   * recherche une ligne éligible sur un éventuel scenario parent
   * @param fei mon fei
   * @param targetKey la clé de widgetLine
   * @returns la WidgetLine issue de papa, mais avec ma clé de scenar et la bonne UK
   */
  private getPreviousLine(fei: FeiVO, targetKey: string): WidgetLine {
    let previousLine: WidgetLine = null;

    // si j'ai un scenario précédent, je vais prendre sa ligne pour remplacer la mienne
    let previousScenario: string = null;
    if (fei.parentScenariiPile?.length > 0) { // j'ai bien un parent (sinon, je suis MAIN)
      previousScenario = fei.parentScenariiPile[0];
    }
    if (!!previousScenario) { // j'ai un héritage
      let previousFEI: FeiVO = this.getFei(fei.computeMode, fei.visionStepLevel, previousScenario);
      previousLine = previousFEI.getWidgetLineForKey(previousScenario, targetKey);

      if (!!previousLine) {
        previousLine = clone(previousLine);
        previousLine.scen = fei.scenario;
        previousLine.uk = [previousLine.scen, previousLine.key].join(WizConstants.KEY_SEPARATOR);

        if (previousLine.cells?.size > 0) { // suppression des cellules calculées, il faut les recréer
          [...previousLine.cells.keys()].forEach(mkey => {
            let editable: boolean = fei.editableKeys.has(mkey);
            if (!editable) {
              previousLine.cells.delete(mkey);
              // console.log('delete no edit', mkey, previousLine.cells); 
            }
          });
        }


      }
    }

    return previousLine;
  }

  /**
   * ordonne à un fei de merger la ligne qui correspond avec son scenario parent
   * appelé en cascade sur le createLine et deleteLine
   * @param fei qui doit effectuer l'opération
   * @param widgetLineKey la clé à manipuler
   * @return la widgetLine mise à jour
   */
  private mergeLineFromPrevious(fei: FeiVO, widgetLineKey: string, deadLineScenario: string = null): WidgetLine {

    // ça c'est moi
    let line: WidgetLine = fei.getWidgetLineForKey(fei.scenario, widgetLineKey);

    // ça, ça retourne une ligne ancêtre, avec mon scenar et ma UK
    let previousLine: WidgetLine = this.getPreviousLine(fei, widgetLineKey);

    if (!line && !!previousLine) { // pas de ligne sur ce scenar, je la crée à partir de l'ancêtre
      line = previousLine;
    } else if (!!line && !!previousLine) { // la ligne existe, son ancêtre aussi
      // console.log('mergeLineFromPrevious both', line);
      // si le scenar de la BL n'est PAS celui de la widgetLine, c'est un override... Je remplace
      if (line.bl.scen !== line.scen) {
        line = previousLine;
      } else {
        const linesFromBDD: Set<string> = FormulaCoreEngine.getPersitedMeasures(fei);
        this.mergeWidgetLine(fei, previousLine, line, linesFromBDD, true, deadLineScenario);
      }
    }

    // console.log('mergeLineFromPrevious final', line);

    return line;
  }

  /**
   * sur retour de commit, suppression de la référence à la widgetLine
   * @param fei cible
   * @param sourceLine IBaseline supprimée 
   * @param loopvo objet de travail contenant les collections à rejouer (pendings) et les résultats CRUD sur les Widgetlines (loopResults)
   * @return le loopresult avec delete alimenté
   */
  private deleteWidgetLine(fei: FeiVO, sourceLine: IBaseline, loopvo: LoopVO): LoopResult {

    // console.log('fei delete', fei.uniqueName, sourceLine);
    let wl: WidgetLine = null;
    if (!!sourceLine) { // prb doublons sur deleteCascade
      wl = fei.getWidgetLineForKey(sourceLine.sc, sourceLine.k);
    }

    if (!wl) {
      console.log('[deleteWidgetLine] wl not found: ', sourceLine.k);
      return null;
    }

    // le résultat dans lequel on va pousser les updates
    let loopResult: LoopResult = DataLooper.getLooperResult(loopvo.loopResults, fei);

    // #### gestion du scenario cible
    fei.deleteLines([wl]); // on supprime l'ancienne

    // préparation du replay sur les dépendances
    Object.values(sourceLine.vs || {}).forEach((ibl: IBaselineValue) => {
      if (ibl.w?.size > 0) {
        ibl.w.forEach((child: WidgetCell) => {
          FormulaCoreEngine.checkPreparePendingMapExist(loopvo.pendings, child.l, child.sc); // contrôle de l'entrée de map
          loopvo.pendings.get(child.l).get(child.sc).add(child);// ajout des cellules souches..
        })
      }
    });

    // et suppression de la matrice
    // le deleteRow va aussi supprimer les entrées de collections
    fei.model.cellMatrixEngine.deleteRow(wl.level, wl.key, wl.scen);
    // let toUpdateCells: Set<WidgetCell> = fei.computeModule.cellMatrixManager.killSerieOnCreateDelete(wl);
    // [...toUpdateCells.values()].forEach((child: WidgetCell) => {
    //   FormulaCoreEngine.checkPreparePendingMapExist(pendings, child.level, child.scen); // contrôle de l'entrée de map
    //   pendings.get(child.level).get(child.scen).add(child);// ajout des cellules souches..
    // })


    let previousLine: WidgetLine = this.getPreviousLine(fei, wl.key);
    if (previousLine) { // on permute si nécessaire
      fei.addLines([previousLine]); // on ajoute la nouvelle
      loopResult.updated.set(previousLine, new Set<WidgetCell>());
    } else {
      loopResult.deleted.add(wl);
    }
    loopResult.deleted.add(wl);

    // #### gestion des scenarii enfants
    // la pile de scenar dans l'ordre, MAIN est déjà dedans
    let allScensPile: string[] = this.model.scenarioEngine.getScenarioByVisionOrderer(this.model.currentVision.code).map(item => item.code);
    allScensPile.forEach((scen: string) => {

      let nextFei: FeiVO = this.getFei(fei.computeMode, wl.level, scen); // prise du fei;
      if (nextFei?.requiredScenario.has(wl.scen)) { // pour chaque scenar enfant, on les fait merger en cascade
        let subLoopResult: LoopResult = DataLooper.getLooperResult(loopvo.loopResults, nextFei);
        let nextWidgetLine: WidgetLine = this.mergeLineFromPrevious(nextFei, wl.key, wl.scen);
        if (nextWidgetLine.bl.scen === sourceLine.sc) { // ce qui est construit, c'est ce qu'on supprime...
          nextFei.deleteLines([nextWidgetLine]);
          // subLoopResult.deleted.add(nextWidgetLine);
        } else {
          nextFei.addLines([nextWidgetLine]);
          subLoopResult.updated.set(nextWidgetLine, new Set<WidgetCell>());
        }
        subLoopResult.deleted.add(nextWidgetLine);
      }
    });

    // TODO TODO vérifier calcul et héritages sur multiscen ??
    // recherche d'un multiScen
    let multiScenFEI: FeiVO = this.getFei(fei.computeMode, wl.level, WizConstants.SCEN_KEY_CODE);
    if (!!multiScenFEI) {
      multiScenFEI.deleteLines([wl]); // on supprime la ligne sur le multiscen
    }

    return loopResult;
  }

  /**
   * sur retour de commit, mise à jour de la widgetLine
   * @param fei cible
   * @param sourceLine Baseline modifiée pour persistence
   */
  public updateLine(fei: FeiVO, sourceLine: Baseline): void {
    BaselineEngine.feedAllKeys(fei, sourceLine, true); // alimentation de la matrice et splitKey
  }

  /**
   * sur retour de commit, creation de la widgetLine
   * @param fei cible
   * @param sourceLine Baseline crée
   * @return la widgetLine concernée, pour recalcul
   */
  public createWidgetLine(fei: FeiVO, sourceLine: Baseline, precursor: WidgetLine, pendings: Map<number, Map<string, Set<WidgetCell>>>): WidgetLineUpdatePack {

    // le pack réel de modif, prise en compte de l'héritage de scenar
    let wlup: WidgetLineUpdatePack = { mainLine: null, children: [] };

    // alimentation de la matrice et splitKey
    BaselineEngine.feedAllKeys(fei, sourceLine);


    // la ligne qu'on veut créer / modifier
    let newLine: WidgetLine = this.baselineToWidgetLine(fei, sourceLine.scen, sourceLine);

    let previousLine: WidgetLine = this.getPreviousLine(fei, newLine.key); // retourne un clone de la widgetLine ancêtre, avec mon scenar
    if (previousLine) { // j'ai un antécédent

      // on a besoin de la vrai ligne pour obtenir les dépendances
      let ibaseline: IBaseline = fei.model.cellMatrixEngine.getMatrix().getRow(sourceLine.level, sourceLine.key, previousLine.bl.scen);
      fei.model.cellMatrixEngine.deleteCollectionByTargetLevelAndScenario(sourceLine.level, sourceLine.scen);
      // let toUpdateCells: Set<WidgetCell> = fei.computeModule.cellMatrixManager.killSerieOnCreateDelete(newLine);
      // [...toUpdateCells.values()].forEach((child: WidgetCell) => {
      //   FormulaCoreEngine.checkPreparePendingMapExist(pendings, child.level, child.scen); // contrôle de l'entrée de map
      //   pendings.get(child.level).get(child.scen).add(child);// ajout des cellules souches..
      // })

      // préparation du replay sur les dépendances
      Object.values(ibaseline?.vs || {}).forEach((ibl: IBaselineValue) => {
        if (ibl.w?.size > 0) {
          ibl.w.forEach((child: WidgetCell) => {
            FormulaCoreEngine.checkPreparePendingMapExist(pendings, child.l, child.sc); // contrôle de l'entrée de map
            pendings.get(child.l).get(child.sc).add(child);// ajout des cellules souches..
          })
        }
      });



      const linesFromBDD: Set<string> = FormulaCoreEngine.getPersitedMeasures(fei); // prise des mesures persistées uniquement
      this.mergeWidgetLine(fei, precursor, newLine, linesFromBDD, true); // et merge
    }

    // ajout aux lines du FEI cible
    fei.addLines([newLine]);
    wlup.mainLine = newLine;


    // #### gestion des scenarii enfants
    // la pile de scenar dans l'ordre, MAIN est déjà dedans
    let allScensPile: string[] = this.model.scenarioEngine.getScenarioByVisionOrderer(this.model.currentVision.code).map(item => item.code);
    allScensPile.forEach((scen: string) => {
      let nextFei: FeiVO = this.getFei(fei.computeMode, newLine.level, scen); // prise du fei;

      if (nextFei?.requiredScenario.has(newLine.scen)) { // pour chaque scenar enfant, on les fait merger en cascade
        let nextWidgetLine: WidgetLine = this.mergeLineFromPrevious(nextFei, newLine.key);
        nextFei.addLines([nextWidgetLine]);
        wlup.children.push([nextFei, nextWidgetLine]);
      }
    });

    // recherche d'un multiScen
    let multiScenFEI: FeiVO = this.getFei(fei.computeMode, newLine.level, WizConstants.SCEN_KEY_CODE);
    if (!!multiScenFEI) {
      multiScenFEI.addLines([newLine]); // on supprime la ligne sur le multiscen
    }


    return wlup;
  }

  /**
   * sur retour de commit, creation ou mise à jour de la widgetCell
   * Possiblement changement de scenario sur la cell
   * @param fei cible
   * @param baselineValue BaselineValue modifiée / créée
   * @return la widgetLine concernée, pour recalcul
   */
  public createOrUpdateCell(fei: FeiVO, baselineValue: BaselineValue): WidgetLineUpdatePack {

    // le pack réel de modif, prise en compte de l'héritage de scenar
    let wlup: WidgetLineUpdatePack = { mainLine: null, children: [] };


    // persistence de la valeur de la baselineValue et prise DE LA BONNE INSTANCE !!
    let cell = fei.model.cellMatrixEngine.addCell(baselineValue.level, baselineValue.key, baselineValue.scen,
      baselineValue.mkey, baselineValue.valor, false); // attention au false... On veut persister la valeur !

    // if(baselineValue.mkey === 'LMA_ED1#I' && baselineValue.key=== "PLAN2SCENAR|PLAN2SCENAR_102|KLE" ) {
    //   console.log('createOrUpdateCell' ,baselineValue.mkey, cell);
    // }

    // console.log('createOrUpdateCell', baselineValue.key, baselineValue.valor, cell);
    // mise à jour de la ligne de widget
    let widgetLine: WidgetLine = fei.getWidgetLineForKey(baselineValue.scen, baselineValue.key)
    // mise à jour du numRev et de la version sur la BaselineValue
    widgetLine.bl.values[baselineValue.mkey] = baselineValue;

    wlup.mainLine = widgetLine;

    // mettre à jour les widgetLines qui héritent du scenar modifié
    let mustPropagate: boolean = false;

    // construction des cellules
    let wc: WidgetCell = widgetLine.cells.get(baselineValue.mkey);
    if (!wc) { // création pure
      wc = { k: baselineValue.key, m: baselineValue.mkey, sc: baselineValue.scen, l: baselineValue.level };
      widgetLine.cells.set(wc.m, wc);
      mustPropagate = true;
      // console.log('createOrUpdateCell no cell', baselineValue.key, baselineValue.valor, cell, widgetLine.cells);
    } else { // simple changement de scenario
      if (wc.sc != baselineValue.scen) {
        mustPropagate = true;
      }
      wc = clone(wc); // on change d'instance
      wc.sc = baselineValue.scen;
      widgetLine.cells.set(wc.m, wc);
      // console.log('createOrUpdateCell exist cell', baselineValue.key, baselineValue.valor, cell, widgetLine.cells);
    }

    if (mustPropagate) {
      // #### gestion des scenarii enfants
      // la pile de scenar dans l'ordre, MAIN est déjà dedans
      let allScensPile: string[] = this.model.scenarioEngine.getScenarioByVisionOrderer(this.model.currentVision.code).map(item => item.code);
      allScensPile.forEach((scen: string) => {
        let nextFei: FeiVO = this.getFei(fei.computeMode, widgetLine.level, scen); // prise du fei;

        if (nextFei?.requiredScenario.has(widgetLine.scen)) { // pour chaque scenar enfant, on les fait merger en cascade
          let nextWidgetLine: WidgetLine = this.mergeLineFromPrevious(nextFei, widgetLine.key);
          nextFei.addLines([nextWidgetLine]);
          wlup.children.push([nextFei, nextWidgetLine]);
        }
      });
    }

    return wlup;
  }

  /* ##############################################################################
          Recalcul
  ############################################################################### */

  /**
   * retour de service de commit.
   * On va jouer les datapacks par levels croissants et par scenarii croissants (héritages)
   * @param loopvo: LoopVO le prototype d'objet pour le looper
   * @param datapack DataPack en retour de service
   * @param filterKeyUpdates modifs sur les filters
   * @param freedataUpdates modifs de valeur de FDV
   */
  public reComputeSteps(
            loopvo: LoopVO,
            datapack: DataPack, 
            filterKeyUpdates: Map<Baseline, Set<string>> = null, 
            freedataUpdates: Map<string, FreedataUpdatePack> = null): LoopVO {

    if (this.model.currentVision.code === WizConstants.defaultVision) { // pas de recalcul si pas de vision
      return;
    }

    // préparation de l'objet de retour
    // const loopvo: LoopVO = { loopResults: new Map(), pendings: new Map(), trace: trace};

    // reset sur la map des pendings de la cellMatrix
    this.model.cellMatrixEngine.clearPending();

    // délégation pour gestion des Freedatas
    if (freedataUpdates?.size > 0) {
      this.recomputeFreedatas(loopvo.pendings, freedataUpdates);
    }

    // ajout des cellules modifiées par changement de filterKeys
    if (!!filterKeyUpdates) {
      [...filterKeyUpdates.keys()].forEach((baseline: Baseline) => { // par baselines
        [...filterKeyUpdates.get(baseline).values()].forEach((filterKeyCode: string) => { // la clé de FDV de filterKey

          // on met à jour la filterKey sur la baseline, ça n'a pas encore été fait
          // let keyVal: string = baseline.fkeyjson[filterKeyCode] || null;
          // BaselineEngine.baselineSetFilterKeyValue(baseline, filterKeyCode, keyVal);

          // la cellule n'existe qu'en cas de dépendances (keyval)
          let cell: IBaselineValue = this.model.cellMatrixEngine.getCell(baseline.level, baseline.key, baseline.scen, filterKeyCode);
          // console.log('filterKeyUpdates', cell, filterKeyCode);
          if (cell?.w?.size > 0) {
            cell.w.forEach((child: WidgetCell) => {
              FormulaCoreEngine.checkPreparePendingMapExist(loopvo.pendings, child.l, child.sc); // contrôle de l'entrée de map
              loopvo.pendings.get(child.l).get(child.sc).add(child);// ajout des cellules souches...
            });
          }

        });
      });
    }

    // ##### prise de l'ordre idéal de résolution des calculs
    // le max step, pour aller de 1 à lui
    let maxLevel: number = Object.values(this.model.currentVision.visionSteps).length;
    // la pile de scenar dans l'ordre, MAIN est déjà dedans
    let allScensPile: string[] = this.model.scenarioEngine.getScenarioByVisionOrderer(this.model.currentVision.code).map(item => item.code);


    // gestion du calcul des baselines et alimentation des pendings et des loopers
    if (!!datapack?.datas) {
      // on boucle pour les résoudre dans le meilleur ordre possible
      for (let i: number = 1; i <= maxLevel; i++) {
        if (datapack.datas[i]) {

          allScensPile.forEach((scen: string) => {
            if (datapack.datas[i][scen]) {
              // console.log('recomputeBaselines', datapack.datas[i], scen, allScensPile);
              this.recomputeBaselines(loopvo, datapack.datas[i][scen]);
            };

          });
        }
      }
    }

    // gestion des collections et sisters
    // on boucle pour les résoudre dans le meilleur ordre possible
    for (let i: number = 1; i <= maxLevel; i++) {
      allScensPile.forEach((scen: string) => {
        let fei: FeiVO = this.getFei(COMPUTE_MODE.LOCAL, i, scen); // prise du fei;
        if (loopvo.loopResults.has(fei)) {
          this.manageBirthAndDeath(loopvo, fei);
        };
      });
    }

    // tour de looper si présence de pendings
    if (loopvo.pendings.size > 0) {
      this.recomputePendings(loopvo);
    }

    // dispatch de l'event de reconstruction
    this.finalizeCompute(loopvo.loopResults, filterKeyUpdates);

    return loopvo;
  }

  /**
   * sur retour de commit, alimentation de la matrice et préparation des pendings sur le périmètre Freedata
   * @param pendings pendingMap
   * @param freedataUpdates Map<string, FreedataUpdatePack>, les updates par code FD
   */
  private recomputeFreedatas(pendings: Map<number, Map<string, Set<WidgetCell>>>, freedataUpdates: Map<string, FreedataUpdatePack>): void {

    // ajout en masse des dépendances sur les FD (fonctions Threshold)
    [...freedataUpdates.keys()].forEach((fdKey: string) => {
      let cellMap: Map<number, Map<string, Set<WidgetCell>>> = this.model.cellMatrixEngine.getFDCells(fdKey)?.cells;
      if (!!cellMap) {
        // existe, on flingue la persistence des paires
        this.model.cellMatrixEngine.getFDCells(fdKey).pairMap = new Map();

        [...cellMap.keys()].forEach((lvl: number) => {
          [...cellMap.get(lvl).keys()].forEach((scenKey: string) => {
            FormulaCoreEngine.checkPreparePendingMapExist(pendings, lvl, scenKey); // contrôle de l'entrée de map

            cellMap.get(lvl).get(scenKey).forEach((childCell: WidgetCell) => { // ça, ce sont les clés dépendantes du FDV
              // ajout des cellules souches...
              pendings.get(lvl).get(scenKey).add(childCell);
            });
          });
        });
      }
    });

    let cell: AkmFDVCell;
    [...freedataUpdates.values()].forEach((fup: FreedataUpdatePack) => { // pour chaque FD dans la map

      // on va inscrire les akmFDVCell de ses FDVs si nécessaire (et supprimer les morts)

      // [...fup.toCreateFDV?.values()].forEach((fdv: FreeDataValue) => {
      //   // create, y'aura pas de dépendances... pour l'instant (possible inscription lors du recompute des baselines)
      //   //inutile tant qu'il n'y a pas de dépendance cell = this.model.cellMatrixEngine.getFDVCell([fdv.freeDataCode, fdv.level].join(ViewData.CONTEXT_SEPARATOR), fdv.code, true);
      // });

      [...fup.toUpdateFDV?.values()].forEach((fdv: FreeDataValue) => {

        // on rince tout sauf la rawVal et les children
        //cell = this.model.cellMatrixEngine.forgetFDVCell([fdv.freeDataCode, fdv.level].join(WizConstants.CONTEXT_SEPARATOR), fdv.code);
        cell = this.model.cellMatrixEngine.forgetFDVCell(fdv.freeDataCode, fdv.code);
        // console.log('data engine recomputeFD', cell);
        
        // va forcer le refresh pour le premier qui demande
        // console.log('recomputeFreedatas2', cell);
        if (!cell) {
          // console.log('recomputeFreedatas missing cell', fup, fdv);
        } else {
          // console.log('recomputeFreedatas exist cell', fup, fdv);
          if (!!cell.ch) {
            [...cell.ch.keys()].forEach((lvl: number) => {
              [...cell.ch.get(lvl).keys()].forEach((scenKey: string) => {
                FormulaCoreEngine.checkPreparePendingMapExist(pendings, lvl, scenKey); // contrôle de l'entrée de map

                cell.ch.get(lvl).get(scenKey).forEach((childCell: WidgetCell) => { // ça, ce sont les clés dépendantes du FDV
                  // ajout des cellules souches...
                  pendings.get(lvl).get(scenKey).add(childCell);
                });
              });
            });
          }
        }


      });

      [...fup.toRemoveFDV?.values()].forEach((fdv: FreeDataValue) => {
        // delete, ajout éventuel des dépendances pour recalcul... avant suppression
        // cell = this.model.cellMatrixEngine.getFDVCell([fdv.freeDataCode, fdv.level].join(WizConstants.CONTEXT_SEPARATOR), fdv.code, false);
        cell = this.model.cellMatrixEngine.getFDVCell(fdv.freeDataCode, fdv.code, false);

        if (!!cell) { // existe, on va nettoyer ses dépendances
          if (!!cell.ch) {
            [...cell.ch.keys()].forEach((lvl: number) => {
              [...cell.ch.get(lvl).keys()].forEach((scenKey: string) => {
                FormulaCoreEngine.checkPreparePendingMapExist(pendings, lvl, scenKey); // contrôle de l'entrée de map

                cell.ch.get(lvl).get(scenKey).forEach((childCell: WidgetCell) => { // ça, ce sont les clés dépendantes du FDV
                  // ajout des cellules souches...
                  pendings.get(lvl).get(scenKey).add(childCell);
                });
              });
            });
          }
          // this.model.cellMatrixEngine.removeFDVCell([fdv.freeDataCode, fdv.level].join(WizConstants.CONTEXT_SEPARATOR), fdv.code);
          this.model.cellMatrixEngine.removeFDVCell(fdv.freeDataCode, fdv.code);
        }
      });
    });

  }

  /**
   * sur retour de service, recalcul des mesures altérées, et de leurs dépendances
   * par levels croissants, par scenarii croissants
   * @param loopvo: LoopVO
   * @param datapack DataPack de retour de service
   */
  public recomputeBaselines(loopvo: LoopVO, datas: DataPackSet): void {

    let fei: FeiVO = this.getFei(COMPUTE_MODE.LOCAL, datas.level, datas.scenario); // prise du fei;

    let baseline: Baseline; // les lignes qu'on modifie / crée
    let baselineValue: BaselineValue; // les values qu'on modifie / crée
    let widgetLine: WidgetLine; // collecte des widgetLines à recalculer
    let udpatedCell: IBaselineValue; // collecte des cellules modifiées pour alimentation des pendings
    let loopResult: LoopResult = DataLooper.getLooperResult(loopvo.loopResults, fei); // le résultat dans lequel on va pousser les updates
    // ######## gestion des DELETE
    // on supprime les morts (les miens sont dedans)
    datas.deleted?.forEach((emt: EntityModificationTrace) => {
      // juste une interface
      let ibaseline: IBaseline = fei.model.cellMatrixEngine.getMatrix().getRow(datas.level, emt.key, datas.scenario, true);
      this.deleteWidgetLine(fei, ibaseline, loopvo);
    })

    // ######## gestion des CREATE / UPDATE sur les BASELINES
    // les baselines peuvent provenir de 2 sources, on les assemble
    let baselinePack: Baseline[] = [];

    // on alimente les nouveaux et les updates (les remotes, modifiés par quelqu'un d'autre)
    datas.remoteBaselines?.forEach((prototype: BaselineLite) => {
      // on prend la baseline sur le dataload (on travaille pas avec une lite)
      let dlo: DataLoadObj = this.model.loadDataService.getDataloadByKey(prototype.v, prototype.l, prototype.scen);
      let exist: Baseline = dlo.localBaselines[prototype.key];
      if(exist) {
        baselinePack.push(exist);
      } else {
        console.log('recomputeBaselines, missing line :', prototype);
      }

    });
    // on alimente mes nouveaux et mes updates
    datas.baselines?.forEach((prototype: Baseline) => {
      // les suppressions on déjà été jouée par le delete depuis les emts
      if (prototype.operation === EM_ACTION.CREATE ||
        prototype.operation === EM_ACTION.UPDATE) {
        baselinePack.push(prototype);
      }
    })

    // moteur des baselines
    baselinePack.forEach((prototype: Baseline) => {

      let wlup: WidgetLineUpdatePack; // le pack de modifs, prise en compte des scenarii hérités

      // la ligne présente dans le pool. Ce n'est pas forcément le bon scenar
      let precursor: WidgetLine = fei.getWidgetLineForKey(prototype.scen, prototype.key);
      if (precursor?.bl.scen === prototype.scen) { // même ligne, même scenar, c'est moi...
        // console.log('recomputeBaselines update', prototype, precursor);
        // on préserve les Values de la source
        if (!!precursor.bl.values) {
          prototype.values = cloneDeep(precursor.bl.values);
        }
        this.updateLine(fei, prototype); // on est bien sur un update, simple modif d'éléments de filterKey

        precursor.bl = prototype; // on remplace la BL dans le précurseur
        widgetLine = precursor;
        widgetLine.rendererMap = null; // on force le refresh sur les rendus

        // on rince les données Dexies
        fei.model.cellMatrixEngine.clearDexieMatrixLine(widgetLine);

        // console.log('recomputeBaselines', widgetLine.byWidgetStringMap);
        // widgetLine.byWidgetStringMap = null; // on rince les valeurs pour filtres génériques

      } else {
        // console.log('recomputeBaselines create', prototype, precursor);
        wlup = this.createWidgetLine(fei, prototype, precursor, loopvo.pendings);
        widgetLine = wlup.mainLine;

        loopResult.created.add(widgetLine);

        // ajout des creates sur les scenarii enfants
        wlup?.children?.forEach((pair: [FeiVO, WidgetLine]) => { // faire plus cibler, uniquement les vrais create... sinon update ??
          let subLoopResult: LoopResult = DataLooper.getLooperResult(loopvo.loopResults, pair[0]);
          subLoopResult.created.add(pair[1]);
        });
      }

      if (!loopResult.updated.has(widgetLine)) { // dans tous les cas, on va calculer les create
        loopResult.updated.set(widgetLine, new Set<WidgetCell>());
      }

      wlup?.children?.forEach((pair: [FeiVO, WidgetLine]) => {
        let subLoopResult: LoopResult = DataLooper.getLooperResult(loopvo.loopResults, pair[0]);
        if (!subLoopResult.updated.has(pair[1])) { // on va recalculer les modifs
          subLoopResult.updated.set(pair[1], new Set<WidgetCell>());
        }
      });

    });

    // ######## gestion des CREATE / UPDATE sur les BASELINE_VALUES
    // les baselineValues peuvent provenir de 2 sources, on les assemble
    let baselineValueUpdatedOrCreatedPack: BaselineValue[] = [];

    // on alimente les mises à jour sur les baselineValues (celles des autres)
    datas.remoteBaselineValues?.forEach((prototype: BaselineValueLite) => {
      // on prend la baseline sur le dataload (on travaille pas avec une lite)
      let dlo: DataLoadObj = this.model.loadDataService.getDataloadByKey(prototype.v, prototype.l, prototype.scen);
      baseline = dlo.localBaselines[prototype.key];
      let mkey: string = [prototype.m, prototype.f].join(WizConstants.CONTEXT_SEPARATOR);
      if(!baseline) {
        console.log('missing remoteBaselineValues', prototype.key,  mkey);
      } else {
        baselineValue = baseline.values[mkey];
        baselineValueUpdatedOrCreatedPack.push(baselineValue);
      }

    });

    // on alimente les mises à jour sur les baselineValues (les miennes)
    datas.baselineValues?.forEach((prototype: BaselineValue) => {
      if (prototype.operation === EM_ACTION.CREATE ||
        prototype.operation === EM_ACTION.UPDATE) {
        baselineValueUpdatedOrCreatedPack.push(prototype);
      }
    });

    // moteur des BASELINE_VALUES
    baselineValueUpdatedOrCreatedPack.forEach((prototype: BaselineValue) => {

      // le pack de modifs, prise en compte des scenarii hérités
      let wlup: WidgetLineUpdatePack = this.createOrUpdateCell(fei, prototype);

      // on met à jour la widgetCell sur la WidgetLine
      widgetLine = wlup.mainLine;

      if (prototype.operation === EM_ACTION.CREATE) {
        // sur un create, on va rejouer tous les pendings
        // et supprimer les collections
        fei.model.cellMatrixEngine.deleteCollectionByTargetLevelAndScenario(widgetLine.level, widgetLine.scen);

        // on va te calculer...
        FormulaCoreEngine.computeLines(fei, [widgetLine]);
      }

      if (!loopResult.updated.has(widgetLine)) { // dans tous les cas, on va calculer les create
        loopResult.updated.set(widgetLine, new Set<WidgetCell>());
      }
      let wc: WidgetCell = widgetLine.cells.get(prototype.mkey);
      loopResult.updated.get(widgetLine).add(wc); // ajout à la pile d'update des Cells pour cette WidgetLine

      // ajout des creates sur les scenarii enfants
      wlup?.children?.forEach((pair: [FeiVO, WidgetLine]) => { // faire plus cibler, uniquement les vrais create... sinon update ??
        let subLoopResult: LoopResult = DataLooper.getLooperResult(loopvo.loopResults, pair[0]);
        subLoopResult.created.add(pair[1]);
      });

      udpatedCell = fei.model.cellMatrixEngine.getCell(prototype.level, prototype.key, prototype.scen, prototype.mkey);

      if (udpatedCell?.w?.size > 0) {
        udpatedCell.w.forEach((child: WidgetCell) => {
          fei.model.cellMatrixEngine.clearDexieMatrixCell(child);
          FormulaCoreEngine.checkPreparePendingMapExist(loopvo.pendings, child.l, child.sc); // contrôle de l'entrée de map
          // console.log('children on recomputeBaseline', child.level, child.scen, child);
          loopvo.pendings.get(child.l).get(child.sc).add(child);// ajout des cellules souches...
        });
      }
      if (udpatedCell?.si?.size > 0) {
        // console.log('series on recomputeBaseline', udpatedCell);
        udpatedCell.si.forEach((sr: SisterResult) => {
          // TODO TODO ici pour recalcul sur childs d'une série
          // un sisterResult identifié... je marque tout le monde... et je le bute, il sera reconstruit par le premier qui passe
          [...sr.sis.values()].forEach((child: WidgetCell) => {
            FormulaCoreEngine.checkPreparePendingMapExist(loopvo.pendings, child.l, child.sc); // contrôle de l'entrée de map
            loopvo.pendings.get(child.l).get(child.sc).add(child);// ajout des cellules souches...
          });
          fei.model.cellMatrixEngine.killSerie(sr.l, sr.sc, sr.magg, sr.sk);
        });
      }

    });

    // recalcul si nécessaire
    if (loopResult.updated.size > 0) {
      // on recalcul les lignes impactées, en silence
      FormulaCoreEngine.computeLines(fei, [...loopResult.updated.keys()]);
    }
  }

  /**
   * gestion des collections, des sisters...
   * et synchronisation des collections
   * Va ajouter les cellules des series sur création
   * @param reComputeWorkingObject objet de travail contenant les collections à rejouer (pendings) et les résultats CRUD sur les Widgetlines (loopResults)
   * @param fei le fei
   */
  public manageBirthAndDeath(loopvo: LoopVO, fei: FeiVO): void {

    let loop: LoopResult = loopvo.loopResults.get(fei);
    
    // les cellules à rejouer
    let toUpdateCells: Set<WidgetCell> = new Set();

    if (loop.created.size > 0) {
      [...loop.created.values()].forEach((wl: WidgetLine) => {

        // contrôle des collections
        let createCells: Set<WidgetCell> = fei.model.cellMatrixEngine.getNeoCollectionDependencies(fei, wl, true);
        [...createCells.values()].forEach((cell: WidgetCell) => {
          toUpdateCells.add(cell);
        });

        // contrôle des séries
        // TODO TODO sur create, mise à jour self marche pas...
        // ?? jouer un compute ??
        let createSerieCells: Set<WidgetCell> = fei.model.cellMatrixEngine.killSerieOnCreateDelete(wl);
        [...createSerieCells.values()].forEach((cell: WidgetCell) => {
          toUpdateCells.add(cell); // check, je suis peut-être enlevé de la pile...
        });
        // console.log('manageBirthAndDeath create on series',createCells, createSerieCells, wl);

      })
    };

    if (loop.deleted.size > 0) {
      [...loop.deleted.values()].forEach((wl: WidgetLine) => {

        // contrôle des collections
        let deleteCollectionCells: Set<WidgetCell> = fei.model.cellMatrixEngine.getNeoCollectionDependencies(fei, wl);
        [...deleteCollectionCells.values()].forEach((cell: WidgetCell) => {
          toUpdateCells.add(cell);
        });

        // contrôle des séries
        let deleteSerieCells: Set<WidgetCell> = fei.model.cellMatrixEngine.killSerieOnCreateDelete(wl);
        [...deleteSerieCells.values()].forEach((cell: WidgetCell) => {
          toUpdateCells.add(cell);
        });
        // console.log('manageBirthAndDeath delete on series', deleteSerieCells);
      })
    };

    [...toUpdateCells.values()].forEach((cell: WidgetCell) => {
      FormulaCoreEngine.checkPreparePendingMapExist(loopvo.pendings, cell.l, cell.sc); // contrôle de l'entrée de map
      loopvo.pendings.get(cell.l).get(cell.sc).add(cell);
    });
  }

  /**
   * tours de boucles de looper pour calculer les mesures en attente
   * @param reComputeWorkingObject objet de travail contenant les collections à rejouer (pendings) et les résultats CRUD sur les Widgetlines (loopResults)
   */
  public recomputePendings(loopvo: LoopVO): void {

    // ajout en masse des pendingCells
    [...loopvo.pendings.keys()].forEach((lvl: number) => {
      [...loopvo.pendings.get(lvl).keys()].forEach((scen: string) => {
        const pendingCells: Set<WidgetCell> = FormulaCoreEngine.checkPreparePendingMapExist(loopvo.pendings, lvl, scen);
        // ajout en masse de tous les cellules en attente de calcul
        this.model.cellMatrixEngine.massAddPendingCell(lvl, scen, pendingCells);
      });
    });

    // doLoop pour chaque FEI concerné
    [...loopvo.pendings.keys()].forEach((level: number) => { // pour chaque level
      [...loopvo.pendings.get(level).keys()].forEach((scen: string) => { // pour chaque scenario 
        let fei: FeiVO = this.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, level, scen);
        // console.log('recomputePendings', fei.visionStepLevel, fei.scenario);

        if (!fei) {
          // console.log('missing FEI for looper', level, scen);
        } else {
          DataLooper.doLoop(fei, loopvo.loopResults);
        }

      });
    });
  }

  /**
   * fin de traitement du recompute, on marque les FEIs comme calculés, et on envoie l'event
   * appelé aussi en cas de rollback sur erreur de service
   * @param loopResults 
   */
  public finalizeCompute(loopResults: Map<FeiVO, LoopResult>, filterKeyUpdates: Map<Baseline, Set<string>>): void {

    [...loopResults.keys()].forEach((fei: FeiVO) => {
      fei.computed = true;
    });

  }

  /**
   * pour overrid"e
   * collecte des dépendances de visions (sur des mesures de type CROSS-FILTER, les mesures sont déjà gérées)
   * @param fei 
   */
  public feedCrossVision(fei: FeiVO): void {
    // [...fei.keyMap.values()].forEach(fkd => {
    //   if(fkd.isMeasure && fkd.datakind === FIELD_KIND.CROSS_FILTER) {
    //     // prise des settings
    //     let sets: any = fkd.measure.settings;
    //     if(!!sets) {
    //       let vision: string = sets.vision;
    //       let level: number = sets.level;
    //       if(vision && level) {
    //         fei.requiredCrossed.add([vision, level].join(WizConstants.CONTEXT_SEPARATOR));
    //       }
    //     }
    //   }

    // });
  }

}
